import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import attendanceService from '../../services/AttendanceService.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, LogIn } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { useTranslation } from 'react-i18next';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';

const AttendanceList = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const userRole = currentUser?.role || currentUser?.role_name;
  const canBulkLogin = ['System Admin', 'Admin', 'Accountant'].includes(userRole);
  const isAgentOrDriver = userRole === 'Agent' || userRole === 'Driver';
  const [attendances, setAttendances] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [periodDisplay, setPeriodDisplay] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchAttendances = useCallback(async () => {
    try {
      setLoading(true);
      
      if (filters.user_id) {
        const response = await attendanceService.getUserAttendances(
          filters.user_id,
          currentPage,
          perPage,
          filters.start_date || null,
          filters.end_date || null
        );
        setAttendances((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
        setTotalPages(response.pagination?.pages || 1);
        setTotalRecords(response.pagination?.total || 0);
      } else {
        const response = await attendanceService.getAllUsersAttendanceSummary(
          filters.year,
          filters.month
        );
        setAttendances(response.data || []);
        setTotalPages(1);
        setTotalRecords(response.data?.length || 0);
        setPeriodDisplay(response.period || '');
      }
    } catch (err) {
      Alert.error('Failed to load attendance records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters.user_id, filters.start_date, filters.end_date, filters.year, filters.month, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchAttendances,
    setCurrentPage,
    currentPage,
    resetPageDeps: [filters.user_id, filters.start_date, filters.end_date, filters.year, filters.month],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      user_id: '',
      start_date: '',
      end_date: '',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.user_id || filters.start_date || filters.end_date;

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.user_id === userId);
    return user ? user.name : userId;
  };

  const handleDelete = async (attendanceId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this attendance record? This action cannot be undone.',
      'Delete Attendance Record'
    );
    
    if (confirmed) {
      try {
        await axiosInstance.delete(`/attendance/${attendanceId}`);
        Alert.success('Attendance record deleted successfully!');
        
        if (attendances.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchAttendances();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete attendance record');
      }
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <DashboardLayout>
              <div>
                <ListPageHeader
                  title={!filters.user_id && periodDisplay ? `Attendance Records - ${periodDisplay}` : 'Attendance Records'}
                  icon={Calendar}
                  titleClassName="text-primary"
                  iconClassName="w-6 h-6 sm:w-7 sm:h-7 text-primary shrink-0"
                  className="mb-4 sm:mb-6"
                >
                  {canBulkLogin && (
                    <Button mobileIconOnly variant="primary" icon={LogIn} onClick={() => navigate('/attendance/bulk-login')}>
                      Bulk Login/Logout
                    </Button>
                  )}
                </ListPageHeader>

                <ListFilterPanel
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={clearFilters}
                  gridClassName={`grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3 ${!isAgentOrDriver ? 'lg:grid-cols-5' : 'lg:grid-cols-3'}`}
                >
                  {!isAgentOrDriver && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
                      <select
                        name="user_id"
                        value={filters.user_id}
                        onChange={handleFilterChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-700"
                      >
                        <option value="">All Users</option>
                        {users.map((user) => (
                          <option key={user.user_id} value={user.user_id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={filters.start_date}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      name="end_date"
                      value={filters.end_date}
                      onChange={handleFilterChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-700"
                    />
                  </div>

                  {!filters.user_id && (
                    <>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Month</label>
                        <select
                          name="month"
                          value={filters.month}
                          onChange={handleFilterChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-700"
                        >
                          {monthNames.map((month, index) => (
                            <option key={index + 1} value={index + 1}>
                              {month}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                        <input
                          type="number"
                          name="year"
                          value={filters.year}
                          onChange={handleFilterChange}
                          min="2020"
                          max="2099"
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm text-gray-700"
                        />
                      </div>
                    </>
                  )}
                </ListFilterPanel>

                {/* Table - Desktop View */}
                <div className="bg-white rounded-xl shadow-sm">
                  {loading && currentPage === 1 ? (
                    <Loader fullScreen={false} size="default" message="Loading attendance records..." />
                  ) : attendances.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">No attendance records found</div>
                  ) : (
                    <>
                      <div className="hidden lg:block">
                        <div className="min-w-full">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {filters.user_id ? (
                                <>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Time</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logout Time</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Hours</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Location</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logout Location</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                                </>
                              ) : (
                                <>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Online Status</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Worked</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Absent</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                                </>
                              )}
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {filters.user_id ? (
                              attendances.map((attendance) => (
                                <tr key={attendance.id} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(attendance.attendance_date)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatTime(attendance.login_time)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {attendance.is_logged_out ? formatTime(attendance.logout_time) : (
                                      <span className="text-green-600 font-medium">Active</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{attendance.work_hours_formatted}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {attendance.login_location ? 
                                      `${attendance.login_location.city}, ${attendance.login_location.state}` : 
                                      'N/A'
                                    }
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {attendance.logout_location ? 
                                      `${attendance.logout_location.city}, ${attendance.logout_location.state}` : 
                                      'N/A'
                                    }
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === attendance.id}
                                      onToggle={() => toggleDropdown(attendance.id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/attendance/view/${attendance.id}`)}
                                      onEdit={() => navigate(`/attendance/edit/${attendance.id}`)}
                                      onDelete={() => handleDelete(attendance.id)}
                                    />
                                  </td>
                                </tr>
                              ))
                            ) : (
                              attendances.map((summary, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div
                                      className="text-sm font-medium text-gray-900 hover:text-orange-600 cursor-pointer transition-colors"
                                      onClick={() => navigate(`/attendance/report/${summary.user_id}`)}
                                    >
                                      {summary.name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{summary.role_name}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-3 h-3 rounded-full ${summary.is_online ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                      <span className="text-sm text-gray-600">{summary.is_online ? 'Online' : 'Offline'}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{summary.days_worked}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{summary.days_absent}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{summary.total_work_hours_formatted}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === `summary-${summary.user_id}`}
                                      onToggle={() => toggleDropdown(`summary-${summary.user_id}`)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/attendance/report/${summary.user_id}`)}
                                      viewLabel="View Report"
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>

                      {/* Mobile Card View */}
                      <div className="lg:hidden space-y-3 sm:space-y-4 p-4">
                        {filters.user_id ? (
                          attendances.map((attendance) => (
                            <div key={attendance.id} className="bg-white border rounded-xl p-4 shadow-sm">
                              <div className="flex justify-between items-start mb-3 gap-2">
                                <div className="min-w-0">
                                  <p className="font-semibold text-gray-900">{formatDate(attendance.attendance_date)}</p>
                                  <p className="text-sm text-gray-600">{attendance.work_hours_formatted}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`px-2 py-1 text-xs rounded-full font-semibold whitespace-nowrap ${
                                    attendance.is_logged_out
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {attendance.is_logged_out ? 'Completed' : 'Active'}
                                  </span>
                                  <RowActionsMenu
                                    isOpen={activeDropdown === attendance.id}
                                    onToggle={() => toggleDropdown(attendance.id)}
                                    onClose={() => setActiveDropdown(null)}
                                    menuRef={dropdownRef}
                                    onView={() => navigate(`/attendance/view/${attendance.id}`)}
                                    onEdit={() => navigate(`/attendance/edit/${attendance.id}`)}
                                    onDelete={() => handleDelete(attendance.id)}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                <div>
                                  <span className="text-gray-500">Login:</span>
                                  <p className="text-gray-900 font-medium">{formatTime(attendance.login_time)}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Logout:</span>
                                  <p className="text-gray-900 font-medium">
                                    {attendance.is_logged_out ? formatTime(attendance.logout_time) : 'Active'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          attendances.map((summary, index) => (
                            <div key={index} className="bg-white border rounded-xl p-4 shadow-sm">
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0">
                                  <h3 className="font-semibold text-gray-900 truncate">{summary.name}</h3>
                                  <p className="text-sm text-gray-600">{summary.role_name}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`px-2 py-1 text-xs rounded-full font-semibold whitespace-nowrap ${
                                    summary.is_online ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {summary.is_online ? 'Online' : 'Offline'}
                                  </span>
                                  <RowActionsMenu
                                    isOpen={activeDropdown === `summary-${summary.user_id}`}
                                    onToggle={() => toggleDropdown(`summary-${summary.user_id}`)}
                                    onClose={() => setActiveDropdown(null)}
                                    menuRef={dropdownRef}
                                    onView={() => navigate(`/attendance/report/${summary.user_id}`)}
                                    viewLabel="View Report"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                                <div>
                                  <span className="text-gray-500">Days Worked:</span>
                                  <p className="text-gray-900 font-semibold">{summary.days_worked}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Days Absent:</span>
                                  <p className="text-gray-900 font-semibold">{summary.days_absent}</p>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-gray-500">Total Hours:</span>
                                  <p className="text-gray-900 font-semibold">{summary.total_work_hours_formatted}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading && currentPage > 1}
                        onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                      />
                    </>
                  )}
                </div>
              </div>
    </DashboardLayout>
  );
};

export default AttendanceList;